import type { FC, ReactNode } from 'react'

import { createContext, useCallback, useContext, useState } from 'react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'error' | 'info'

type Toast = {
    id: number
    tone: ToastTone
    message: string
}

type PushToast = (message: string, tone?: ToastTone) => void

interface ToastProviderProps {
    children: ReactNode
}

const ToastContext = createContext<PushToast>(() => undefined)

const toneClasses: Record<ToastTone, string> = {
    success: 'border-success/40 bg-[#10251a] text-[#c9f7dc]',
    error: 'border-danger/40 bg-[#2a1210] text-[#ffd2cc]',
    info: 'border-border bg-popover text-foreground'
}

let nextId = 1

export const ToastProvider: FC<ToastProviderProps> = ({ children }): ReactNode => {
    const [toasts, setToasts] = useState<Toast[]>([])

    const push = useCallback<PushToast>((message, tone = 'info') => {
        const id = nextId++

        setToasts((current) => [...current.slice(-3), { id, tone, message }])
        window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_500)
    }, [])

    return (
        <ToastContext.Provider value={push}>
            {children}

            <div aria-live='polite' className='pointer-events-none fixed inset-x-0 bottom-5 z-[80] flex flex-col items-center gap-2 px-4'>
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        role={toast.tone === 'error' ? 'alert' : 'status'}
                        className={cn('df-pop pointer-events-auto w-full max-w-sm rounded-xl border px-4 py-3 text-sm font-medium shadow-2xl shadow-black/50', toneClasses[toast.tone])}
                    >
                        {toast.message}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): PushToast => useContext(ToastContext)
